// chatbotLangGraph.tsx — MexaScreen production chat → LangGraph POST /chat (auto token, panel sync).
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BACKEND_URL, getAccessTokenForOrchestrator, getAuthHeaders } from './backendClient';
import { onAuthStateChange } from './components/authForm';
import { applyOrchestratorChatSync } from './orchestratorSync';
import { STEP_LABELS, stepLabel, } from './langGraphChatTypes';
import { appendStepFiche, resolveStepFiche, shouldShowStepFiche, stepFicheDedupKey, } from './langGraphStepFiche';
import { appendChatNotices, noticeIntroMessage, shouldHideLaunchNoticeForActionBanner, resolveChatNotices, shouldSuppressOperationalBubble, } from './langGraphChatNotice';
import PipelineDebugDrawer from './langGraphPipelineDebug';
import { LANGGRAPH_PROJECT_ID_KEY, getActiveSessionId, syncLangGraphProjectId, updateProjectTarget, } from './sessionProjects';
const LANGGRAPH_URL = process.env.LANGGRAPH_URL || 'http://localhost:8000';
// ── INFRA: FRONTEND -> ALB --> BACKEND -> AGENTCORE (Option A) ────────────────────────────────────────────
// Dev local  : ORCHESTRATOR_VIA_BACKEND=false → POST http://localhost:8000/chat (FastAPI direct)
// Prod / test: ORCHESTRATOR_VIA_BACKEND=true  → POST {BACKEND_URL}/orchestrator/chat via ALB
const USE_BACKEND_ORCHESTRATOR_PROXY = process.env.ORCHESTRATOR_VIA_BACKEND === 'true';
const CHAT_URL = USE_BACKEND_ORCHESTRATOR_PROXY
    ? `${BACKEND_URL}/orchestrator/chat`
    : `${LANGGRAPH_URL}/chat`;
const THREAD_URL = USE_BACKEND_ORCHESTRATOR_PROXY
    ? `${BACKEND_URL}/orchestrator/chat/thread`
    : `${LANGGRAPH_URL}/chat/thread`;
/** Dual peptide field (number or sequence). Left-panel sequence-only uses index.html. */
const PEPTIDE_OR_NUMBER_PLACEHOLDER_EN = '1 or dTRP1b-dASP-PRO-dVAL-LEU1b';
const PEPTIDE_OR_NUMBER_PLACEHOLDER_FR = '1 ou dTRP1b-dASP-PRO-dVAL-LEU1b';
const PEPTIDE_SEQUENCE_PLACEHOLDER = 'dTRP1b-dASP-PRO-dVAL-LEU1b';
function getPanelPeptidePlaceholder() {
    const panel = document.getElementById('peptide-input');
    const fromDom = ((panel === null || panel === void 0 ? void 0 : panel.placeholder) || '').trim();
    return fromDom || PEPTIDE_SEQUENCE_PLACEHOLDER;
}
function peptideNumberOrSequencePlaceholder(en) {
    return en ? PEPTIDE_OR_NUMBER_PLACEHOLDER_EN : PEPTIDE_OR_NUMBER_PLACEHOLDER_FR;
}
/** Same rule as peptideForm.ts: spaces → hyphens while typing. */
function attachPeptideSpaceToDash(input) {
    input.addEventListener('input', () => {
        var _a;
        const cursor = (_a = input.selectionStart) !== null && _a !== void 0 ? _a : 0;
        const value = input.value;
        const next = value.replace(/\s+/g, '-');
        if (next === value)
            return;
        input.value = next;
        const diff = next.length - value.length;
        input.setSelectionRange(cursor + diff, cursor + diff);
    });
}
/** Same normalization as left-panel submit (trim + spaces → dashes). */
function normalizePanelPeptideSequence(raw) {
    return raw.trim().replace(/\s+/g, '-');
}
/** Site chrome is always English. Chatbot UI follows backend ui_lang. */
let chatUiLang = 'en';
function resolveUiLang() {
    return 'en';
}
function chatLang(data) {
    var _a;
    return (_a = data === null || data === void 0 ? void 0 : data.ui_lang) !== null && _a !== void 0 ? _a : chatUiLang;
}
function t(fr, en) {
    return chatUiLang === 'fr' ? fr : en;
}
const WELCOME_EMOJIS = ['🧬', '🧪', '⚗️', '⚛️'];
function fallbackWelcomeMessage() {
    const emoji = WELCOME_EMOJIS[Math.floor(Math.random() * WELCOME_EMOJIS.length)];
    return t(`Bienvenue sur MexaScreen ${emoji} Je vous accompagne dans la pipeline Mexa de drug design. Décrivez votre projet, ou une structure ou un PDB si vous en avez déjà un en tête.`, `Welcome to MexaScreen ${emoji} I walk you through the Mexa drug-design pipeline. Describe your project, or a structure or PDB if you already have one in mind.`);
}
const CLEAR_STEP1_LABEL = STEP_LABELS.project_scoping;
let clearChatHistoryInFlight = false;
// Stores the MexaScreen session number ("1", "2"…).
// Server builds LangGraph config: actor_id = Cognito sub, thread_id = sessionId.
// Same contract on SQLite (local) and AgentCoreMemorySaver (prod).
// Written by sessionProjects.ts when the user switches project in the navbar.
function getLoggedInUsername() {
    var _a;
    try {
        const raw = localStorage.getItem('currentUser');
        if (!raw)
            return null;
        return (_a = JSON.parse(raw).username) !== null && _a !== void 0 ? _a : null;
    }
    catch (_b) {
        return null;
    }
}
function resolveActiveProjectId() {
    var _a;
    const username = getLoggedInUsername();
    if (username) {
        return String(getActiveSessionId(username));
    }
    return (_a = localStorage.getItem(LANGGRAPH_PROJECT_ID_KEY)) !== null && _a !== void 0 ? _a : '1';
}
const ACTION_BANNER_ID = 'langgraph-action-banner';
function getChatHistory() {
    return document.getElementById('chat-history');
}
/** Scroll after layout — banners/fiches need a frame or the bottom stays clipped. */
function scrollChatToBottom() {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const run = () => {
        chatHistory.scrollTop = chatHistory.scrollHeight;
    };
    run();
    requestAnimationFrame(() => requestAnimationFrame(run));
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** Escape then render common markdown (**bold**, *italic*, `code`) for chat bubbles. */
function formatChatMarkdown(text) {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');
}
function appendChatLine(role, text) {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const p = document.createElement('p');
    p.innerHTML = `<span class="${role}"></span>${formatChatMarkdown(text)}`;
    chatHistory.appendChild(p);
    scrollChatToBottom();
}
function setTyping(show) {
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const oldTyping = chatHistory.querySelector('.typing');
    if (oldTyping)
        oldTyping.remove();
    if (!show)
        return;
    const typing = document.createElement('p');
    typing.className = 'typing';
    chatHistory.appendChild(typing);
    scrollChatToBottom();
}
function removeActionBanner() {
    var _a;
    (_a = document.getElementById(ACTION_BANNER_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
function applyPhaseUI(phase) {
    const inputField = document.getElementById('chat-input');
    const sendButton = document.getElementById('chat-send');
    // Keep chat enabled between steps: resource ops + fiche corrections via natural language.
    const locked = false;
    if (inputField) {
        inputField.disabled = locked;
        inputField.placeholder =
            phase === 'between_steps'
                ? 'Free message (e.g. check receptor) or click Continue…'
                : phase === 'job_pending'
                    ? 'Job in progress — you can send a message or wait…'
                    : 'Type your message...';
        inputField.style.opacity = locked ? '0.5' : '1';
    }
    if (sendButton)
        sendButton.disabled = locked;
}
function isHiddenMachineUserText(text) {
    const t = text.trim();
    if (!t)
        return false;
    // Form / confirm / sync payloads — never a user-visible bubble.
    if (t.startsWith('__'))
        return true;
    if (/^passe le r[eé]cepteur\b.+\ben target\b/i.test(t))
        return true;
    return false;
}
function setSendingLocked(locked, _phase) {
    const sendButton = document.getElementById('chat-send');
    if (sendButton) {
        sendButton.disabled = locked;
    }
    const inputField = document.getElementById('chat-input');
    if (inputField) {
        inputField.disabled = locked;
    }
}
function shouldShowActionBanner(phase, currentStep, opts) {
    var _a;
    if (opts === null || opts === void 0 ? void 0 : opts.hideActionBanner)
        return false;
    if ((_a = opts === null || opts === void 0 ? void 0 : opts.notices) === null || _a === void 0 ? void 0 : _a.some((n) => n.kind === 'delete'))
        return false;
    if (phase === 'brief_complete' || phase === 'between_steps')
        return true;
    return phase === 'job_pending' && (currentStep === 'receptor_preparation' ||
        currentStep === 'peptide_design' ||
        currentStep === 'docking' ||
        currentStep === 'screening');
}
function mountContinueOnlyBanner(banner, chatHistory, buttonHtml, onClick) {
    var _a;
    banner.classList.add('langgraph-action-banner-continue-only');
    banner.innerHTML = buttonHtml;
    (_a = banner.querySelector('.langgraph-action-btn')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
        removeActionBanner();
        onClick();
    });
    chatHistory.appendChild(banner);
    scrollChatToBottom();
}
function showActionBanner(phase, nextLabel, currentStep, snap, selectedPdb, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, uiLang = 'en', stepFicheShown = false) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    removeActionBanner();
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const en = uiLang === 'en';
    const sl = (step) => stepLabel(step, uiLang);
    const continueBtn = (label) => en
        ? `▶ Continue → ${escapeHtml(label)}`
        : `▶ Continuer → ${escapeHtml(label)}`;
    const banner = document.createElement('div');
    banner.className = 'langgraph-action-banner';
    banner.id = ACTION_BANNER_ID;
    const receptorId = snap === null || snap === void 0 ? void 0 : snap.receptor_id;
    const peptideId = snap === null || snap === void 0 ? void 0 : snap.peptide_id;
    const bindingSite = (snap === null || snap === void 0 ? void 0 : snap.binding_site) || undefined;
    const dockingId = snap === null || snap === void 0 ? void 0 : snap.docking_id;
    const briefFromSnap = (snap === null || snap === void 0 ? void 0 : snap.project_brief) || undefined;
    const confirmedFields = Array.isArray(briefFromSnap === null || briefFromSnap === void 0 ? void 0 : briefFromSnap.confirmed_fields)
        ? briefFromSnap.confirmed_fields
        : [];
    const requiredBrief = ['target', 'pathology', 'strategy', 'constraints'];
    const briefComplete = requiredBrief.every((f) => confirmedFields.indexOf(f) !== -1);
    const mainPdb = (typeof (snap === null || snap === void 0 ? void 0 : snap.main_pdb) === 'string' ? snap.main_pdb.trim().toUpperCase() : '') || '';
    const mountMainTargetPdbChoice = () => {
        var _a, _b, _c, _d;
        const targetFiche = ((_a = snap === null || snap === void 0 ? void 0 : snap.target_fiche) !== null && _a !== void 0 ? _a : null);
        const targetContext = ((_b = snap === null || snap === void 0 ? void 0 : snap.target_context) !== null && _b !== void 0 ? _b : null);
        const selection = (_c = targetContext === null || targetContext === void 0 ? void 0 : targetContext.pdb_selection) !== null && _c !== void 0 ? _c : {};
        const suggestedPdb = String(selection.suggested_pdb || '').trim().toUpperCase();
        const target = (typeof (briefFromSnap === null || briefFromSnap === void 0 ? void 0 : briefFromSnap.target) === 'string' && briefFromSnap.target.trim()) ||
            (typeof (targetFiche === null || targetFiche === void 0 ? void 0 : targetFiche.target) === 'string' && targetFiche.target.trim()) ||
            (en ? 'your therapeutic target' : 'ta target thérapeutique');
        const targetLabel = selection.status === 'no_target'
            ? (en ? 'No target yet' : 'Aucune target définie')
            : target;
        if (selection.status === 'suggested' && suggestedPdb) {
            if (!stepFicheShown) {
                appendStepFiche({
                    fiche_type: 'pdb',
                    title: en ? 'PDB Selection' : 'Sélection PDB',
                    rows: [
                        { label: en ? 'Selected PDB' : 'PDB sélectionné', value: suggestedPdb },
                        {
                            label: 'Note',
                            value: en
                                ? "First PDB associated with the target by Target Biology and UniProt. Provisional default: the project's scientific constraints have not yet been compared to select the best structure."
                                : "Premier PDB associé à la target par Target Biology et UniProt. Choix par défaut provisoire : les contraintes scientifiques du projet ne sont pas encore comparées pour sélectionner la meilleure structure.",
                        },
                    ],
                });
            }
            mountContinueOnlyBanner(banner, chatHistory, `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(sl('receptor_preparation'))}</button>`, onApprove);
            return;
        }
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-receptor', 'langgraph-main-target-choice');
        banner.innerHTML = `
      <div class="langgraph-receptor-target-context">
        <span>${en ? 'PDB Selection' : 'Sélection PDB'}</span>
        <strong>${escapeHtml(targetLabel)}</strong>
      </div>
      <span class="langgraph-action-label">
        ${selection.status === 'no_target'
            ? (en
                ? 'This step will compare RCSB structures by resolution, completeness, mutations and ligand context. Define a biological target first.'
                : 'Cette étape comparera les structures RCSB selon la résolution, la complétude, les mutations et le contexte ligand. Définis d’abord une target biologique.')
            : selection.status === 'suggested' && suggestedPdb
                ? (en
                    ? `<strong>${escapeHtml(suggestedPdb)}</strong> is the first PDB reference returned by UniProt. It is a provisional default, not a scientific recommendation.`
                    : `<strong>${escapeHtml(suggestedPdb)}</strong> est le premier PDB référencé par UniProt. C’est un choix provisoire, pas une recommandation scientifique.`)
                : (en
                    ? 'A future version will compare RCSB structures against the project criteria. For now, choose the receptor PDB during preparation.'
                    : 'Une prochaine version comparera les structures RCSB selon les critères du projet. Pour le moment, choisis le PDB lors de la préparation du récepteur.')}
      </span>
      ${selection.status !== 'no_target' ? `
        <div class="langgraph-receptor-next-action">
          <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${selection.status === 'suggested' && suggestedPdb
            ? (en
                ? `✓ Confirm ${escapeHtml(suggestedPdb)} &amp; continue →`
                : `✓ Confirmer ${escapeHtml(suggestedPdb)} &amp; continuer →`)
            : continueBtn(sl('receptor_preparation'))}</button>
        </div>
      ` : ''}
    `;
        (_d = banner.querySelector('.langgraph-action-btn-primary')) === null || _d === void 0 ? void 0 : _d.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
    };
    const mountReceptorPreparationForm = (allowContinue) => {
        var _a, _b;
        const selected = (selectedPdb ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' ? snap.selected_pdb : '') ||
            '').trim().toUpperCase();
        const isOffTargetLaunch = Boolean(mainPdb);
        // Never prefill the current Main Target into an Off-Target launch field.
        const initialPdb = isOffTargetLaunch && selected === mainPdb ? '' : selected;
        const nextStep = sl('binding_site');
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-receptor');
        banner.innerHTML = `
      ${isOffTargetLaunch ? `
        <div class="langgraph-receptor-target-context">
          <span>${en ? 'Main Target' : 'Target principale'}</span>
          <strong>${escapeHtml(mainPdb)}</strong>
        </div>
      ` : ''}
      <span class="langgraph-action-label">
        ${isOffTargetLaunch
            ? (en
                ? 'Enter another PDB to prepare it as an <strong>Off-Target</strong>.'
                : 'Saisis un autre PDB pour le préparer comme <strong>Off-Target</strong>.')
            : (en
                ? 'No Main Target is defined in this session. Launch a receptor preparation; you can then choose whether it should become the Main Target.'
                : 'Aucune Target principale n’est définie dans cette session. Lance la préparation d’un récepteur ; tu pourras ensuite choisir s’il doit devenir la Target principale.')}
      </span>
      <div class="langgraph-inline-launch-row">
        <input
          type="text"
          id="langgraph-pdb-input"
          class="langgraph-pdb-input"
          placeholder="ex. 4DLQ"
          maxlength="4"
          value="${escapeHtml(initialPdb)}"
          spellcheck="false"
          autocomplete="off"
          aria-label="${isOffTargetLaunch ? 'Off-Target PDB' : 'Receptor PDB'}"
        />
        <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb" data-rec-act="launch">${isOffTargetLaunch
            ? (en ? '⚗️ Launch Off-Target' : '⚗️ Lancer l’Off-Target')
            : (en ? '⚗️ Launch preparation' : '⚗️ Lancer la préparation')}</button>
      </div>
      ${allowContinue ? `
        <div class="langgraph-receptor-next-action">
          <button type="button" class="langgraph-action-btn langgraph-action-btn-primary" data-rec-act="continue">${continueBtn(nextStep)}</button>
        </div>
      ` : ''}
    `;
        (_a = banner.querySelector('[data-rec-act="launch"]')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-pdb-input');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || '').trim().toUpperCase();
            if (!/^[0-9][A-Z0-9]{3}$/.test(raw)) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchPdb === null || onLaunchPdb === void 0 ? void 0 : onLaunchPdb(raw);
        });
        (_b = banner.querySelector('[data-rec-act="continue"]')) === null || _b === void 0 ? void 0 : _b.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
    };
    if (currentStep === 'pdb_selection' && !mainPdb && phase === 'between_steps') {
        mountMainTargetPdbChoice();
        return;
    }
    else if (phase === 'brief_complete' && briefComplete) {
        const validateBtn = en ? '✓ Validate &amp; continue →' : '✓ Valider &amp; continuer →';
        if (stepFicheShown) {
            mountContinueOnlyBanner(banner, chatHistory, `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${validateBtn}</button>`, onApprove);
            return;
        }
        banner.innerHTML = `
      <span class="langgraph-action-label">${en
            ? 'All fields confirmed. Edit via chat, or launch step 2.'
            : "Tous les champs confirmés. Modifie via le chat, ou lance l'étape 2."}</span>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${validateBtn}</button>
    `;
    }
    else if ((phase === 'brief_complete' || phase === 'between_steps') &&
        currentStep === 'project_scoping' &&
        !briefComplete) {
        // Fiche + chat bubble already explain what's missing — no duplicate banner.
        return;
    }
    else if (phase === 'brief_complete' && !briefComplete) {
        // Same on any step: incomplete brief — bubble + fiche are enough.
        return;
    }
    else if (currentStep === 'target_biology' &&
        phase === 'between_steps') {
        const targetFiche = ((_a = snap === null || snap === void 0 ? void 0 : snap.target_fiche) !== null && _a !== void 0 ? _a : null);
        const ficheFilled = Boolean((targetFiche === null || targetFiche === void 0 ? void 0 : targetFiche.uniprot_id) ||
            (targetFiche === null || targetFiche === void 0 ? void 0 : targetFiche.mechanism) ||
            ((_b = targetFiche === null || targetFiche === void 0 ? void 0 : targetFiche.details) === null || _b === void 0 ? void 0 : _b.function) ||
            ((_c = targetFiche === null || targetFiche === void 0 ? void 0 : targetFiche.details) === null || _c === void 0 ? void 0 : _c.source));
        if (!ficheFilled) {
            return;
        }
        const nextLabel = sl('pdb_selection');
        if (stepFicheShown) {
            mountContinueOnlyBanner(banner, chatHistory, `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(nextLabel)}</button>`, onApprove);
            return;
        }
        banner.innerHTML = `
      <span class="langgraph-action-label">${en
            ? `Ready for <strong>${escapeHtml(nextLabel)}</strong>?`
            : `Prêt pour <strong>${escapeHtml(nextLabel)}</strong> ?`}</span>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(nextLabel)}</button>
    `;
        (_d = banner.querySelector('.langgraph-action-btn')) === null || _d === void 0 ? void 0 : _d.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'receptor_preparation' &&
        phase === 'between_steps' &&
        receptorId != null &&
        receptorId !== '') {
        mountReceptorPreparationForm(true);
        return;
    }
    else if (currentStep === 'receptor_preparation' &&
        (phase === 'job_pending' || (phase === 'between_steps' && !receptorId))) {
        mountReceptorPreparationForm(false);
        return;
    }
    else if (currentStep === 'peptide_design' &&
        (phase === 'between_steps' || phase === 'job_pending')) {
        const askingNewPeptide = Boolean((snap === null || snap === void 0 ? void 0 : snap.keep_action_banner) || (snap === null || snap === void 0 ? void 0 : snap.suppress_step_fiche));
        const hasPeptide = !askingNewPeptide && peptideId != null && peptideId !== '';
        const pendingSeq = (typeof (snap === null || snap === void 0 ? void 0 : snap.pending_peptide_sequence) === 'string' && snap.pending_peptide_sequence.trim()) ||
            '';
        // Prefill for edit/relaunch; leave empty when launching another peptide so the user can type.
        const initialSeq = hasPeptide ? '' : pendingSeq;
        const peptidePlaceholder = escapeHtml(getPanelPeptidePlaceholder() || PEPTIDE_SEQUENCE_PLACEHOLDER);
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-peptide');
        banner.innerHTML = `
      <span class="langgraph-action-label">${hasPeptide
            ? (en
                ? `Launch another peptide, or continue to ${sl('docking')}:`
                : `Lancer un autre peptide, ou continuer vers ${sl('docking')} :`)
            : (en
                ? 'Peptide sequence for CPBuilder:'
                : 'Séquence peptidique pour CPBuilder :')}</span>
      <textarea
        id="langgraph-peptide-input"
        class="langgraph-peptide-sequence-input"
        placeholder="${peptidePlaceholder}"
        maxlength="300"
        rows="2"
        spellcheck="false"
        autocomplete="off"
      >${escapeHtml(initialSeq)}</textarea>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb" data-peptide-act="launch">${hasPeptide
            ? (en ? '🧬 Launch another peptide' : '🧬 Lancer un autre peptide')
            : (en ? '🧬 Launch peptide' : '🧬 Lancer le peptide')}</button>
      ${hasPeptide
            ? `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary" data-peptide-act="continue">${continueBtn(sl('docking'))}</button>`
            : ''}
`;
        const peptideEl = banner.querySelector('#langgraph-peptide-input');
        if (peptideEl) {
            attachPeptideSpaceToDash(peptideEl);
            peptideEl.removeAttribute('readonly');
            peptideEl.removeAttribute('disabled');
        }
        (_e = banner.querySelector('[data-peptide-act="launch"]')) === null || _e === void 0 ? void 0 : _e.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-peptide-input');
            // Same rules as left panel: non-empty after trim; spaces already → dashes.
            const raw = normalizePanelPeptideSequence((input === null || input === void 0 ? void 0 : input.value) || '');
            if (!raw) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchPeptide === null || onLaunchPeptide === void 0 ? void 0 : onLaunchPeptide(raw);
        });
        (_f = banner.querySelector('[data-peptide-act="continue"]')) === null || _f === void 0 ? void 0 : _f.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'binding_site' &&
        phase === 'between_steps' &&
        !bindingSite) {
        const totalRaw = snap === null || snap === void 0 ? void 0 : snap.available_binding_site_count;
        const total = typeof totalRaw === 'number' && totalRaw > 0 ? totalRaw : 0;
        // Hide select banner while receptor/pockets are not ready yet (avoid 1…N fake range).
        if (total <= 0) {
            return;
        }
        const rangeLabel = `1…${total}`;
        banner.classList.add('langgraph-action-banner-pdb');
        banner.innerHTML = `
      <span class="langgraph-action-label">${en ? `Site number (${rangeLabel}):` : `Numéro du site (${rangeLabel}) :`}</span>
      <input
        type="text"
        id="langgraph-binding-input"
        class="langgraph-peptide-input"
        placeholder="${en ? 'e.g. 1' : 'ex. 1'}"
        maxlength="12"
        value="1"
        spellcheck="false"
        autocomplete="off"
      />
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb">${en ? '🎯 Select site' : '🎯 Sélectionner le site'}</button>
    `;
        (_g = banner.querySelector('.langgraph-action-btn-pdb')) === null || _g === void 0 ? void 0 : _g.addEventListener('click', () => {
            const input = banner.querySelector('#langgraph-binding-input');
            const raw = ((input === null || input === void 0 ? void 0 : input.value) || '').trim();
            if (!raw) {
                input === null || input === void 0 ? void 0 : input.focus();
                return;
            }
            removeActionBanner();
            onLaunchBindingSite === null || onLaunchBindingSite === void 0 ? void 0 : onLaunchBindingSite(`__binding_site_form_submit__ ${raw}`);
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'screening' &&
        (phase === 'between_steps' || phase === 'job_pending')) {
        const site = (snap === null || snap === void 0 ? void 0 : snap.binding_site) || undefined;
        const hasScreening = (snap === null || snap === void 0 ? void 0 : snap.screening_id) != null && snap.screening_id !== '';
        const formHints = (snap === null || snap === void 0 ? void 0 : snap.screening_form_hints) || undefined;
        const hintPdb = (typeof (formHints === null || formHints === void 0 ? void 0 : formHints.receptor) === 'string' && formHints.receptor) ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' && snap.selected_pdb) ||
            (typeof (site === null || site === void 0 ? void 0 : site.pdb_id) === 'string' ? site.pdb_id : '') ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.main_pdb) === 'string' && snap.main_pdb) ||
            '';
        const hintSite = (typeof (formHints === null || formHints === void 0 ? void 0 : formHints.site) === 'string' && formHints.site) ||
            ((site === null || site === void 0 ? void 0 : site.menu_index) != null && site.menu_index !== '' && site.menu_index !== -1
                ? String(site.menu_index)
                : (site === null || site === void 0 ? void 0 : site.site_index) != null && site.site_index !== '' && site.site_index !== -1
                    ? String(site.site_index === 0 ? 1 : site.site_index)
                    : '');
        const ackPrefill = Boolean(formHints === null || formHints === void 0 ? void 0 : formHints.ack);
        const totalRaw = (_h = snap === null || snap === void 0 ? void 0 : snap.available_binding_site_count) !== null && _h !== void 0 ? _h : site === null || site === void 0 ? void 0 : site.n_binding_sites;
        const total = typeof totalRaw === 'number' && totalRaw > 0 ? totalRaw : 0;
        const rangeHint = total > 0 ? ` (1…${total})` : '';
        const label = ackPrefill
            ? (en
                ? 'Confirmation required — verify Target and site before launching screening:'
                : 'Confirmation demandée — vérifie Target et site avant de lancer le screening :')
            : hasScreening
                ? (en
                    ? `Launch another screening, or continue to ${sl('peptide_design')}:`
                    : `Lancer un autre screening, ou continuer vers ${sl('peptide_design')} :`)
                : (en
                    ? `Select Target and binding site${escapeHtml(rangeHint)}:`
                    : `Sélectionner Target et binding site${escapeHtml(rangeHint)} :`);
        const subLabel = ackPrefill
            ? (en
                ? 'Select Target (PDB) and binding site for screening'
                : 'Sélectionner Target (PDB) et binding site pour le screening')
            : '';
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-docking');
        banner.innerHTML = `
      <span class="langgraph-action-label">${label}</span>
      ${subLabel ? `<span class="langgraph-action-sublabel">${subLabel}</span>` : ''}
      <div class="langgraph-docking-fields">
        <label class="langgraph-docking-field">
          <span>Target</span>
          <input
            type="text"
            id="langgraph-screening-receptor"
            class="langgraph-pdb-input"
            placeholder="${escapeHtml(hintPdb || 'ex. 1TQN')}"
            maxlength="24"
            value="${escapeHtml(hintPdb)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
        <label class="langgraph-docking-field">
          <span>Site</span>
          <input
            type="text"
            id="langgraph-screening-site"
            class="langgraph-pdb-input"
            placeholder="${escapeHtml(hintSite || 'ex. 1')}"
            maxlength="12"
            value="${escapeHtml(hintSite)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
      </div>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb" data-screen-act="launch">${hasScreening && !ackPrefill
            ? (en ? '🚀 Launch another screening' : '🚀 Lancer un autre screening')
            : (en ? '🚀 Launch screening' : '🚀 Lancer le screening')}</button>
      ${hasScreening && !ackPrefill
            ? `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary" data-screen-act="continue">${continueBtn(sl('peptide_design'))}</button>`
            : `<button type="button" class="langgraph-action-btn langgraph-action-btn-secondary" data-screen-act="skip">${en ? `Skip → ${sl('peptide_design')}` : `Passer → ${sl('peptide_design')}`}</button>`}
    `;
        (_j = banner.querySelector('[data-screen-act="launch"]')) === null || _j === void 0 ? void 0 : _j.addEventListener('click', () => {
            const recEl = banner.querySelector('#langgraph-screening-receptor');
            const siteEl = banner.querySelector('#langgraph-screening-site');
            const rec = ((recEl === null || recEl === void 0 ? void 0 : recEl.value) || '').trim();
            const raw = ((siteEl === null || siteEl === void 0 ? void 0 : siteEl.value) || '').trim();
            if (!rec) {
                recEl === null || recEl === void 0 ? void 0 : recEl.focus();
                return;
            }
            if (!raw) {
                siteEl === null || siteEl === void 0 ? void 0 : siteEl.focus();
                return;
            }
            removeActionBanner();
            onLaunchBindingSite === null || onLaunchBindingSite === void 0 ? void 0 : onLaunchBindingSite(`__screening_form_submit__ lance screening receptor ${rec} site ${raw}`);
        });
        (_k = banner.querySelector('[data-screen-act="continue"]')) === null || _k === void 0 ? void 0 : _k.addEventListener('click', () => {
            removeActionBanner();
            onApprove();
        });
        (_l = banner.querySelector('[data-screen-act="skip"]')) === null || _l === void 0 ? void 0 : _l.addEventListener('click', () => {
            removeActionBanner();
            onLaunchBindingSite === null || onLaunchBindingSite === void 0 ? void 0 : onLaunchBindingSite('passer');
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (currentStep === 'docking' && (phase === 'between_steps' || phase === 'job_pending')) {
        const site = (snap === null || snap === void 0 ? void 0 : snap.binding_site) || undefined;
        const pendingJobs = Array.isArray(snap === null || snap === void 0 ? void 0 : snap.pending_jobs) ? snap.pending_jobs : [];
        let dockStatus = '';
        for (let i = pendingJobs.length - 1; i >= 0; i -= 1) {
            const job = pendingJobs[i];
            if (job && job.type === 'docking') {
                dockStatus = String(job.status || '').toLowerCase();
                break;
            }
        }
        const dockFailed = (dockStatus === 'failed' ||
            dockStatus === 'job_submission_failed' ||
            dockStatus === 'cancelled' ||
            dockStatus === 'stopped' ||
            dockStatus === 'error');
        const hasDocking = dockingId != null && dockingId !== '';
        const formHints = (snap === null || snap === void 0 ? void 0 : snap.docking_form_hints) || undefined;
        const hintPdb = (typeof (formHints === null || formHints === void 0 ? void 0 : formHints.receptor) === 'string' && formHints.receptor) ||
            (typeof (snap === null || snap === void 0 ? void 0 : snap.selected_pdb) === 'string' && snap.selected_pdb) ||
            (typeof (site === null || site === void 0 ? void 0 : site.pdb_id) === 'string' ? site.pdb_id : '') ||
            '';
        const hintSeq = (typeof (formHints === null || formHints === void 0 ? void 0 : formHints.peptide) === 'string' && formHints.peptide) ||
            '';
        const hintSite = (typeof (formHints === null || formHints === void 0 ? void 0 : formHints.site) === 'string' && formHints.site) ||
            ((site === null || site === void 0 ? void 0 : site.menu_index) != null && site.menu_index !== '' && site.menu_index !== -1
                ? String(site.menu_index)
                : (site === null || site === void 0 ? void 0 : site.site_index) != null && site.site_index !== '' && site.site_index !== -1
                    ? String(site.site_index)
                    : '');
        const ackPrefill = Boolean(formHints === null || formHints === void 0 ? void 0 : formHints.ack);
        const label = dockFailed
            ? (en
                ? 'Docking failed — adjust params and relaunch:'
                : 'Docking en échec — ajuste et relance :')
            : ackPrefill
                ? (en
                    ? 'Confirmation required — verify the info before launching docking:'
                    : 'Confirmation demandée — vérifie les infos avant de lancer le docking :')
                : hasDocking
                    ? (en
                        ? 'Launch another docking:'
                        : 'Lancer un autre docking :')
                    : (en
                        ? 'Select receptor, peptide and binding site for docking:'
                        : 'Sélectionner récepteur, peptide et binding site pour le docking :');
        const subLabel = ackPrefill
            ? (en
                ? 'Select receptor, peptide and binding site for docking'
                : 'Sélectionner récepteur, peptide et binding site pour le docking')
            : '';
        const launchLabel = dockFailed
            ? (en ? '🚀 Relaunch docking' : '🚀 Relancer le docking')
            : hasDocking
                ? (en ? '🚀 Launch another docking' : '🚀 Lancer un autre docking')
                : (en ? '🚀 Launch docking' : '🚀 Lancer le docking');
        banner.classList.add('langgraph-action-banner-pdb', 'langgraph-action-banner-docking');
        banner.innerHTML = `
      <span class="langgraph-action-label">${label}</span>
      ${subLabel ? `<span class="langgraph-action-sublabel">${subLabel}</span>` : ''}
      <div class="langgraph-docking-fields">
        <label class="langgraph-docking-field">
          <span>${en ? 'Receptor' : 'Récepteur'}</span>
          <input
            type="text"
            id="langgraph-docking-receptor"
            class="langgraph-pdb-input"
            placeholder="${escapeHtml(hintPdb || 'ex. 1MFG')}"
            maxlength="24"
            value="${escapeHtml(hintPdb)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
        <label class="langgraph-docking-field">
          <span>Peptide</span>
          <input
            type="text"
            id="langgraph-docking-peptide"
            class="langgraph-peptide-input langgraph-peptide-or-number-input"
            placeholder="${escapeHtml(peptideNumberOrSequencePlaceholder(en))}"
            maxlength="80"
            value="${escapeHtml(hintSeq)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
        <label class="langgraph-docking-field">
          <span>Site</span>
          <input
            type="text"
            id="langgraph-docking-site"
            class="langgraph-pdb-input"
            placeholder="${escapeHtml(hintSite || 'ex. 1')}"
            maxlength="12"
            value="${escapeHtml(hintSite)}"
            spellcheck="false"
            autocomplete="off"
          />
        </label>
      </div>
      <button type="button" class="langgraph-action-btn langgraph-action-btn-pdb">${launchLabel}</button>
    `;
        (_m = banner.querySelector('.langgraph-action-btn-pdb')) === null || _m === void 0 ? void 0 : _m.addEventListener('click', () => {
            const recEl = banner.querySelector('#langgraph-docking-receptor');
            const pepEl = banner.querySelector('#langgraph-docking-peptide');
            const siteEl = banner.querySelector('#langgraph-docking-site');
            const rec = ((recEl === null || recEl === void 0 ? void 0 : recEl.value) || '').trim();
            const pepRaw = ((pepEl === null || pepEl === void 0 ? void 0 : pepEl.value) || '').trim();
            const bs = ((siteEl === null || siteEl === void 0 ? void 0 : siteEl.value) || '').trim();
            if (!rec) {
                recEl === null || recEl === void 0 ? void 0 : recEl.focus();
                return;
            }
            if (!pepRaw) {
                pepEl === null || pepEl === void 0 ? void 0 : pepEl.focus();
                return;
            }
            if (!bs) {
                siteEl === null || siteEl === void 0 ? void 0 : siteEl.focus();
                return;
            }
            removeActionBanner();
            const recRef = /^\d+$/.test(rec) ? rec : rec;
            const pepRef = /^\d+$/.test(pepRaw) ? `#${pepRaw}` : pepRaw;
            onLaunchDocking === null || onLaunchDocking === void 0 ? void 0 : onLaunchDocking(`__docking_form_submit__ lance docking receptor ${recRef} peptide ${pepRef} binding site ${bs}`);
        });
        chatHistory.appendChild(banner);
        scrollChatToBottom();
        return;
    }
    else if (phase === 'between_steps') {
        if (currentStep === 'pdb_selection') {
            const label = nextLabel || sl('receptor_preparation') || (en ? 'Receptor Preparation' : 'Préparation récepteur');
            if (stepFicheShown) {
                mountContinueOnlyBanner(banner, chatHistory, `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(label)}</button>`, onApprove);
                return;
            }
            banner.innerHTML = `
        <span class="langgraph-action-label">${en
                ? 'PDB saved. Continue to receptor preparation to enter the PDB and launch the job.'
                : 'PDB enregistré. Passez à la préparation récepteur pour saisir le PDB et lancer le job.'}</span>
        <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(label)}</button>
      `;
        }
        else {
            const label = nextLabel || (currentStep ? sl(currentStep) : '') || (en ? 'next step' : 'étape suivante');
            if (stepFicheShown) {
                mountContinueOnlyBanner(banner, chatHistory, `<button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(label)}</button>`, onApprove);
                return;
            }
            banner.innerHTML = `
        <span class="langgraph-action-label">${en
                ? `Ready for <strong>${escapeHtml(label)}</strong>?`
                : `Prêt pour <strong>${escapeHtml(label)}</strong> ?`}</span>
        <button type="button" class="langgraph-action-btn langgraph-action-btn-primary">${continueBtn(label)}</button>
      `;
        }
    }
    else {
        return;
    }
    (_o = banner.querySelector('.langgraph-action-btn')) === null || _o === void 0 ? void 0 : _o.addEventListener('click', () => {
        removeActionBanner();
        onApprove();
    });
    chatHistory.appendChild(banner);
    scrollChatToBottom();
}
function handleChatResponse(data, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const phase = ((_a = data.phase) !== null && _a !== void 0 ? _a : 'collecting_brief');
    chatUiLang = (_b = data.ui_lang) !== null && _b !== void 0 ? _b : 'en';
    const notices = resolveChatNotices(data);
    const reply = (_d = (_c = data.ai_response) !== null && _c !== void 0 ? _c : data.message) !== null && _d !== void 0 ? _d : '';
    const suppressBubble = shouldSuppressOperationalBubble(data, notices);
    const hasConfirmCard = ((_e = data.actions) !== null && _e !== void 0 ? _e : []).some((a) => a.action === 'navigate_to_step' ||
        a.action === 'stay_on_step' ||
        a.action === 'confirm_docking_launch' ||
        a.action === 'cancel_docking_launch' ||
        a.action === 'confirm_delete_receptor' ||
        a.action === 'confirm_delete_peptide' ||
        a.action === 'cancel_delete' ||
        a.action === 'confirm_set_main_target' ||
        a.action === 'cancel_set_main_target' ||
        a.action === 'confirm_target_mismatch' ||
        a.action === 'cancel_target_mismatch');
    const hasSetMainConfirm = ((_f = data.actions) !== null && _f !== void 0 ? _f : []).some((a) => a.action === 'confirm_set_main_target');
    const hasTargetMismatchConfirm = ((_g = data.actions) !== null && _g !== void 0 ? _g : []).some((a) => a.action === 'confirm_target_mismatch');
    const stepForBanner = (_h = data.active_step) !== null && _h !== void 0 ? _h : data.current_step;
    const hideLaunchNotice = shouldHideLaunchNoticeForActionBanner(notices, stepForBanner);
    if (notices.length && suppressBubble) {
        const intro = noticeIntroMessage(notices, {
            currentStep: stepForBanner,
        });
        if (intro) {
            appendChatLine('bot', intro);
        }
        else if (reply.trim() && !hasConfirmCard) {
            // Launch / reuse / error / check / job-status → card only (no stale Step N bubble).
            const opsKinds = new Set(['launch', 'reuse', 'error', 'check', 'pending', 'delete']);
            const opsOnly = notices.every((n) => opsKinds.has(n.kind));
            const reuseOnly = notices.some((n) => n.kind === 'reuse');
            const jobStatusOnly = notices.every((n) => n.kind === 'info' && /job status/i.test(n.title || ''));
            // Never re-attach a leftover "Step 3 — PDB Selection" dump next to check_all.
            const looksLikeStepDump = /^step\s*\d+/i.test(reply.trim());
            if (!opsOnly && !reuseOnly && !jobStatusOnly && !looksLikeStepDump) {
                appendChatLine('bot', reply);
            }
        }
    }
    // Always show the operational status card; relaunch / Continue banner follows.
    if (notices.length && !hideLaunchNotice) {
        appendChatNotices(notices);
    }
    else if (notices.length && hideLaunchNotice) {
        appendChatNotices(notices.filter((n) => n.kind !== 'launch'));
    }
    // Confirm card / Target modal already carries the text — don't duplicate as a bubble.
    // Set-main confirm uses the panel modal (no chat bubble).
    const reuseNotice = notices.some((n) => n.kind === 'reuse');
    if (reply && !suppressBubble && !hasConfirmCard && !hasSetMainConfirm && !hasTargetMismatchConfirm && !reuseNotice) {
        appendChatLine('bot', reply);
    }
    let stepFicheShown = false;
    if (shouldShowStepFiche(phase)) {
        const fiche = resolveStepFiche(data);
        if (fiche) {
            appendStepFiche(fiche);
            lastStepFicheKeyRef.current = stepFicheDedupKey(data, fiche);
            stepFicheShown = true;
        }
    }
    // Action banners (binding-site select, Continuer, …) always AFTER the bot
    // message / fiche for this turn — never before the sites list.
    const snap = Object.assign(Object.assign({}, ((_j = data.state_snapshot) !== null && _j !== void 0 ? _j : {})), { target_fiche: (_k = data.target_fiche) !== null && _k !== void 0 ? _k : (_l = data.state_snapshot) === null || _l === void 0 ? void 0 : _l.target_fiche, project_brief: (_m = data.project_brief) !== null && _m !== void 0 ? _m : (_o = data.state_snapshot) === null || _o === void 0 ? void 0 : _o.project_brief });
    const showBanner = shouldShowActionBanner(phase, (_p = data.active_step) !== null && _p !== void 0 ? _p : data.current_step, {
        hideActionBanner: Boolean(snap.hide_action_banner),
        notices,
    });
    if (showBanner) {
        // Navigation confirm card replaces Continuer — don't stack both.
        const hasConfirmCard = ((_q = data.actions) !== null && _q !== void 0 ? _q : []).some((a) => a.action === 'navigate_to_step' ||
            a.action === 'stay_on_step' ||
            a.action === 'confirm_docking_launch' ||
            a.action === 'cancel_docking_launch' ||
            a.action === 'confirm_delete_receptor' ||
            a.action === 'confirm_delete_peptide' ||
            a.action === 'cancel_delete' ||
            a.action === 'confirm_set_main_target' ||
            a.action === 'cancel_set_main_target' ||
            a.action === 'confirm_target_mismatch' ||
            a.action === 'cancel_target_mismatch');
        if (!hasConfirmCard) {
            showActionBanner(phase, (_r = data.next_step_label) !== null && _r !== void 0 ? _r : '', (_s = data.active_step) !== null && _s !== void 0 ? _s : data.current_step, snap, data.selected_pdb, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, (_t = data.ui_lang) !== null && _t !== void 0 ? _t : 'en', stepFicheShown);
        }
        else {
            removeActionBanner();
        }
    }
    else {
        // e.g. delete card — drop any leftover Launch/Continue banner from prior turns.
        removeActionBanner();
    }
    // Target switch / therapeutic vs structure mismatch → panel modal (not a chat card).
    if (hasTargetMismatchConfirm) {
        void promptTargetMismatchConfirm(data);
    }
    else if (hasSetMainConfirm) {
        void promptSetMainTargetConfirm(data);
    }
    else {
        // Clickable confirmation actions (e.g. navigation) — independent from Continuer banner.
        showConfirmActions(data);
    }
    applyPhaseUI(phase);
    return phase;
}
const CONFIRM_ACTIONS_ID = 'langgraph-confirm-actions';
function removeConfirmActions() {
    var _a;
    (_a = document.getElementById(CONFIRM_ACTIONS_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
/** Same modal as Off-Target logo click — confirm before promoting Main Target from chat. */
function promptSetMainTargetConfirm(data) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        const confirmAction = ((_a = data.actions) !== null && _a !== void 0 ? _a : []).find((a) => a.action === 'confirm_set_main_target');
        const cancelAction = ((_b = data.actions) !== null && _b !== void 0 ? _b : []).find((a) => a.action === 'cancel_set_main_target');
        if (!confirmAction)
            return;
        const showConfirm = window.showConfirmationModal;
        const pending = ((_e = (_c = data.pending_action) !== null && _c !== void 0 ? _c : (_d = data.state_snapshot) === null || _d === void 0 ? void 0 : _d.pending_action) !== null && _e !== void 0 ? _e : {});
        const pendingBody = String(pending.body || '').trim();
        const pendingNote = String(pending.note || '').trim();
        const pendingTitle = String(pending.title || '').trim();
        const pdb = String(pending.pdb_id || '').trim() ||
            (typeof data.main_pdb === 'string' && data.main_pdb.trim()) ||
            (typeof data.selected_pdb === 'string' && data.selected_pdb.trim()) ||
            (typeof ((_f = data.state_snapshot) === null || _f === void 0 ? void 0 : _f.main_pdb) === 'string' && data.state_snapshot.main_pdb.trim()) ||
            '';
        const fromReply = (data.ai_response || data.message || '').match(/\b([0-9][A-Za-z0-9]{3})\b/);
        const labelPdb = ((fromReply === null || fromReply === void 0 ? void 0 : fromReply[1]) || pdb || 'this receptor').toUpperCase();
        const body = pendingBody ||
            (data.ai_response || data.message || '').trim().replace(/\*\*/g, '') ||
            `Make PDB ${labelPdb} the session Main Target?`;
        const note = pendingNote ||
            `If you confirm:\n` +
                `• ${labelPdb} becomes the Main Target\n` +
                `• The previous Target becomes Off-Target (its jobs keep their history)\n` +
                `• Pipeline defaults reset: the current binding site, screening and docking context will no longer apply to this Target\n` +
                `• Target Biology will be rebuilt from ${labelPdb}\n` +
                `• Existing receptors, peptides, dockings, screenings, and results will not be deleted`;
        if (!showConfirm) {
            // Fallback: chat buttons if modal unavailable.
            showConfirmActions(data);
            return;
        }
        const confirmed = yield showConfirm(pendingTitle || confirmAction.label || 'Switch Main Target?', body, confirmAction.label || 'Set as Target', (cancelAction === null || cancelAction === void 0 ? void 0 : cancelAction.label) || 'Cancel', note);
        const sendConfirm = window.__langGraphSendConfirm;
        if (confirmed) {
            sendConfirm === null || sendConfirm === void 0 ? void 0 : sendConfirm({
                label: confirmAction.label || 'Set as Target',
                action: 'confirm_set_main_target',
                type: 'confirm_action',
            });
        }
        else {
            sendConfirm === null || sendConfirm === void 0 ? void 0 : sendConfirm({
                label: (cancelAction === null || cancelAction === void 0 ? void 0 : cancelAction.label) || 'Cancel',
                action: 'cancel_set_main_target',
                type: 'dismiss_action',
            });
        }
    });
}
/** Replace therapeutic target — Cancel change or Replace (no keep-both). */
function targetMismatchPending(data) {
    var _a, _b;
    const snap = (_a = data.state_snapshot) !== null && _a !== void 0 ? _a : {};
    const pending = (_b = data.pending_action) !== null && _b !== void 0 ? _b : snap.pending_action;
    return pending && typeof pending === 'object' ? pending : {};
}
function looksLikeFicheCta(text) {
    return /all fields are confirmed|validate & continue|tous les champs sont confirmés|valider & continuer/i.test(text);
}
function promptTargetMismatchConfirm(data) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const confirmAction = ((_a = data.actions) !== null && _a !== void 0 ? _a : []).find((a) => a.action === 'confirm_target_mismatch');
        const cancelAction = ((_b = data.actions) !== null && _b !== void 0 ? _b : []).find((a) => a.action === 'cancel_target_mismatch');
        if (!confirmAction)
            return;
        const showConfirm = window.showConfirmationModal;
        const pending = targetMismatchPending(data);
        const newTarget = String(pending.proposed_target || '').trim();
        const currentTarget = String(pending.current_target || '').trim();
        const pdb = String(pending.main_pdb || '').trim();
        const uniprotId = String(pending.uniprot_id || '').trim();
        const pendingBody = String(pending.body || '').trim();
        const pendingNote = String(pending.note || '').trim();
        const pendingTitle = String(pending.title || '').trim();
        const fromAi = (data.ai_response || data.message || '').trim().replace(/\*\*/g, '');
        const body = pendingBody ||
            (!looksLikeFicheCta(fromAi) && fromAi) ||
            t(`Tu as saisi ${newTarget || '?'}, mais le PDB Target principale ${pdb || '?'} `
                + `est identifié comme ${currentTarget || '?'}`
                + `${uniprotId ? ` (UniProt ${uniprotId})` : ''}. Ces targets ne correspondent pas.`, `You entered ${newTarget || '?'}, but the current Main Target PDB ${pdb || '?'} `
                + `is identified as ${currentTarget || '?'}`
                + `${uniprotId ? ` (UniProt ${uniprotId})` : ''}. These targets do not match.`);
        if (!showConfirm) {
            showConfirmActions(data);
            return;
        }
        const confirmed = yield showConfirm(pendingTitle || t('Remplacer la target thérapeutique ?', 'Replace therapeutic target?'), body, confirmAction.label || t(`Remplacer par ${newTarget || '?'}`, `Replace with ${newTarget || '?'}`), (cancelAction === null || cancelAction === void 0 ? void 0 : cancelAction.label) || t('Annuler le changement', 'Cancel change'), pendingNote || undefined);
        const sendConfirm = window.__langGraphSendConfirm;
        if (confirmed) {
            sendConfirm === null || sendConfirm === void 0 ? void 0 : sendConfirm({
                label: confirmAction.label || `Replace with ${newTarget || 'target'}`,
                action: 'confirm_target_mismatch',
                type: 'confirm_action',
            });
        }
        else {
            sendConfirm === null || sendConfirm === void 0 ? void 0 : sendConfirm({
                label: (cancelAction === null || cancelAction === void 0 ? void 0 : cancelAction.label) || 'Cancel change',
                action: 'cancel_target_mismatch',
                type: 'dismiss_action',
            });
        }
    });
}
function showConfirmActions(data) {
    var _a;
    removeConfirmActions();
    const actions = ((_a = data.actions) !== null && _a !== void 0 ? _a : []).filter((a) => a.action !== 'retry_step_launch' &&
        a.action !== 'skip_failed_step' &&
        a.action !== 'confirm_set_main_target' &&
        a.action !== 'cancel_set_main_target' &&
        a.action !== 'confirm_target_mismatch' &&
        a.action !== 'cancel_target_mismatch');
    if (!actions.length)
        return;
    const chatHistory = getChatHistory();
    if (!chatHistory)
        return;
    const isNav = actions.some((a) => a.action === 'navigate_to_step') ||
        actions.some((a) => a.action === 'stay_on_step');
    const isDockingConfirm = actions.some((a) => a.action === 'confirm_docking_launch') ||
        actions.some((a) => a.action === 'cancel_docking_launch');
    const isDeleteConfirm = actions.some((a) => a.action === 'confirm_delete_receptor') ||
        actions.some((a) => a.action === 'confirm_delete_peptide') ||
        actions.some((a) => a.action === 'cancel_delete');
    const lang = chatLang(data);
    const en = lang === 'en';
    const wrap = document.createElement('div');
    wrap.id = CONFIRM_ACTIONS_ID;
    if (isNav || isDockingConfirm || isDeleteConfirm) {
        wrap.className = isDeleteConfirm
            ? 'langgraph-nav-confirm langgraph-delete-confirm'
            : 'langgraph-nav-confirm';
        const title = document.createElement('div');
        title.className = 'langgraph-nav-confirm-title';
        if (isDeleteConfirm) {
            title.textContent = en ? 'Confirm deletion' : 'Confirmer la suppression';
        }
        else if (isDockingConfirm) {
            title.textContent = en ? 'Confirm docking' : 'Confirmer le docking';
        }
        else {
            const locked = actions.some((a) => a.action === 'stay_on_step' && !actions.some((x) => x.action === 'navigate_to_step'));
            title.textContent = locked
                ? (en ? 'Step unavailable' : 'Étape indisponible')
                : (en ? 'Change step' : 'Changer d\'étape');
        }
        wrap.appendChild(title);
        const body = document.createElement('p');
        body.className = 'langgraph-nav-confirm-body';
        const raw = (data.ai_response || data.message || '').trim() ||
            (isDockingConfirm
                ? (en
                    ? 'Check PDB, sequence, and binding site before launching.'
                    : 'Vérifie le PDB, la séquence et le site de liaison avant de lancer.')
                : (en
                    ? 'Confirm to change step, or stay on the current step.'
                    : 'Confirme pour changer d\'étape, ou reste sur l\'étape actuelle.'));
        // Keep the card concise — first two paragraphs max; render **bold** markdown.
        body.innerHTML = formatChatMarkdown(raw)
            .split(/\n\n+/)
            .slice(0, 2)
            .join('<br><br>');
        wrap.appendChild(body);
        const btns = document.createElement('div');
        btns.className = 'langgraph-nav-confirm-actions';
        // Cancel left, confirm right when flex-end.
        const ordered = isDeleteConfirm
            ? [...actions].sort((a, b) => {
                if (a.action === 'cancel_delete')
                    return -1;
                if (b.action === 'cancel_delete')
                    return 1;
                return 0;
            })
            : actions;
        for (const action of ordered) {
            if (!(action === null || action === void 0 ? void 0 : action.label))
                continue;
            const btn = document.createElement('button');
            btn.type = 'button';
            const isDeleteConfirmBtn = isDeleteConfirm &&
                action.action !== 'cancel_delete' &&
                action.action.startsWith('confirm_delete');
            const isPrimary = action.action === 'navigate_to_step' ||
                action.action === 'confirm_docking_launch' ||
                (action.type === 'confirm_action' && action.action !== 'cancel_delete');
            const isSecondary = action.action === 'stay_on_step' ||
                action.action === 'cancel_docking_launch' ||
                action.action === 'cancel_delete';
            btn.className = isDeleteConfirmBtn
                ? 'langgraph-action-btn langgraph-action-btn-danger'
                : isSecondary
                    ? 'langgraph-action-btn langgraph-action-btn-secondary'
                    : isPrimary
                        ? 'langgraph-action-btn langgraph-action-btn-primary'
                        : 'langgraph-action-btn langgraph-action-btn-secondary';
            btn.textContent = action.label;
            btn.addEventListener('click', () => {
                removeConfirmActions();
                const send = window.__langGraphSendConfirm;
                send === null || send === void 0 ? void 0 : send({
                    label: action.label,
                    action: action.action,
                    type: action.type,
                });
            });
            btns.appendChild(btn);
        }
        wrap.appendChild(btns);
    }
    else {
        wrap.className = 'langgraph-action-banner';
        const label = document.createElement('span');
        label.className = 'langgraph-action-label';
        label.textContent = 'Confirm action:';
        wrap.appendChild(label);
        for (const action of actions) {
            if (!(action === null || action === void 0 ? void 0 : action.label))
                continue;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'langgraph-action-btn langgraph-action-btn-primary';
            btn.textContent = action.label;
            btn.addEventListener('click', () => {
                removeConfirmActions();
                const send = window.__langGraphSendConfirm;
                send === null || send === void 0 ? void 0 : send({
                    label: action.label,
                    action: action.action,
                    type: action.type,
                });
            });
            wrap.appendChild(btn);
        }
    }
    chatHistory.appendChild(wrap);
    scrollChatToBottom();
}
/** Replay checkpoint messages + restore phase banner/fiche without re-sending to the graph. */
function applyThreadRestore(data, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const chatHistory = getChatHistory();
    if (chatHistory)
        chatHistory.innerHTML = '';
    removeActionBanner();
    let lastHistoricalFicheJson = '';
    for (const msg of (_a = data.messages) !== null && _a !== void 0 ? _a : []) {
        const content = (msg.content || '').trim();
        // Hide machine Target-sync lines from replay (panel already applied the change).
        const hideUserSync = msg.role === 'user' && isHiddenMachineUserText(content);
        if (content && !hideUserSync)
            appendChatLine(msg.role, msg.content);
        if ((_b = msg.notices) === null || _b === void 0 ? void 0 : _b.length)
            appendChatNotices(msg.notices);
        for (const fiche of (_c = msg.fiches) !== null && _c !== void 0 ? _c : []) {
            appendStepFiche(fiche);
            lastHistoricalFicheJson = JSON.stringify(fiche);
        }
    }
    const chat = data.chat;
    if (!chat) {
        applyPhaseUI('collecting_brief');
        return 'collecting_brief';
    }
    chatUiLang = (_d = chat.ui_lang) !== null && _d !== void 0 ? _d : 'en';
    const phase = ((_e = chat.phase) !== null && _e !== void 0 ? _e : 'collecting_brief');
    let stepFicheShown = false;
    if (shouldShowStepFiche(phase)) {
        const fiche = resolveStepFiche(chat);
        if (fiche && JSON.stringify(fiche) !== lastHistoricalFicheJson) {
            appendStepFiche(fiche);
            lastStepFicheKeyRef.current = stepFicheDedupKey(chat, fiche);
            stepFicheShown = true;
        }
    }
    const restoreSnap = Object.assign(Object.assign({}, ((_f = chat.state_snapshot) !== null && _f !== void 0 ? _f : {})), { target_fiche: (_g = chat.target_fiche) !== null && _g !== void 0 ? _g : (_h = chat.state_snapshot) === null || _h === void 0 ? void 0 : _h.target_fiche, project_brief: (_j = chat.project_brief) !== null && _j !== void 0 ? _j : (_k = chat.state_snapshot) === null || _k === void 0 ? void 0 : _k.project_brief });
    const restoreNotices = ((_l = chat.chat_notices) !== null && _l !== void 0 ? _l : []);
    const showBanner = shouldShowActionBanner(phase, (_m = chat.active_step) !== null && _m !== void 0 ? _m : chat.current_step, {
        hideActionBanner: Boolean(restoreSnap.hide_action_banner),
        notices: restoreNotices,
    });
    if (showBanner) {
        const hasConfirmCard = ((_o = chat.actions) !== null && _o !== void 0 ? _o : []).some((a) => a.action === 'navigate_to_step' ||
            a.action === 'stay_on_step' ||
            a.action === 'confirm_docking_launch' ||
            a.action === 'cancel_docking_launch' ||
            a.action === 'confirm_delete_receptor' ||
            a.action === 'confirm_delete_peptide' ||
            a.action === 'cancel_delete' ||
            a.action === 'confirm_set_main_target' ||
            a.action === 'cancel_set_main_target' ||
            a.action === 'confirm_target_mismatch' ||
            a.action === 'cancel_target_mismatch');
        if (!hasConfirmCard) {
            showActionBanner(phase, (_p = chat.next_step_label) !== null && _p !== void 0 ? _p : '', (_q = chat.active_step) !== null && _q !== void 0 ? _q : chat.current_step, restoreSnap, chat.selected_pdb, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, (_r = chat.ui_lang) !== null && _r !== void 0 ? _r : 'en', stepFicheShown);
        }
        else {
            removeActionBanner();
        }
    }
    else {
        removeActionBanner();
    }
    const restoreHasMismatch = ((_s = chat.actions) !== null && _s !== void 0 ? _s : []).some((a) => a.action === 'confirm_target_mismatch');
    const restoreHasSetMain = ((_t = chat.actions) !== null && _t !== void 0 ? _t : []).some((a) => a.action === 'confirm_set_main_target');
    if (restoreHasMismatch) {
        void promptTargetMismatchConfirm(chat);
    }
    else if (restoreHasSetMain) {
        void promptSetMainTargetConfirm(chat);
    }
    else {
        showConfirmActions(chat);
    }
    applyPhaseUI(phase);
    return phase;
}
function restoreChatThread(projectId, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef, setLastResponse, currentPhaseRef, signal, isCurrent) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const chatHistory = getChatHistory();
        if (!chatHistory)
            return;
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken || signal.aborted || !isCurrent())
                return;
            setTyping(true);
            const response = yield fetch(`${THREAD_URL}?project_id=${encodeURIComponent(projectId)}`, {
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
                signal,
            });
            const data = yield response.json().catch(() => ({ exists: false }));
            if (signal.aborted || !isCurrent())
                return;
            if (!response.ok || !data.exists) {
                chatHistory.innerHTML = '';
                removeActionBanner();
                currentPhaseRef.current = 'collecting_brief';
                applyPhaseUI('collecting_brief');
                setLastResponse(null);
                const welcome = (data.welcome || '').trim() || fallbackWelcomeMessage();
                appendChatLine('bot', welcome);
                return;
            }
            const username = getLoggedInUsername();
            const activeId = username
                ? getActiveSessionId(username)
                : parseInt(projectId, 10);
            if (username && ((_b = (_a = data.chat) === null || _a === void 0 ? void 0 : _a.project_brief) === null || _b === void 0 ? void 0 : _b.target)) {
                updateProjectTarget(username, activeId, data.chat.project_brief.target);
            }
            setLastResponse((_c = data.chat) !== null && _c !== void 0 ? _c : null);
            const phase = applyThreadRestore(data, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef);
            currentPhaseRef.current = phase;
            if (data.chat) {
                yield ((_d = window.applyOrchestratorChatSync) !== null && _d !== void 0 ? _d : applyOrchestratorChatSync)(data.chat);
            }
        }
        catch (err) {
            if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
                return;
            console.warn('[ChatbotLangGraph] Failed to restore chat thread:', err);
            if (chatHistory)
                chatHistory.innerHTML = '';
            removeActionBanner();
            currentPhaseRef.current = 'collecting_brief';
            applyPhaseUI('collecting_brief');
            setLastResponse(null);
            appendChatLine('bot', fallbackWelcomeMessage());
        }
        finally {
            if (isCurrent())
                setTyping(false);
        }
    });
}
function clearChatHistory(projectId, setLastResponse, currentPhaseRef, lastStepFicheKeyRef) {
    return __awaiter(this, void 0, void 0, function* () {
        if (clearChatHistoryInFlight)
            return;
        const showConfirm = window.showConfirmationModal;
        if (!showConfirm) {
            console.error('[ChatbotLangGraph] showConfirmationModal not available');
            return;
        }
        // Let the trash-button click finish before opening the modal.
        yield new Promise((r) => window.setTimeout(r, 0));
        const confirmed = yield showConfirm(t("Effacer l'historique du chat ?", 'Clear chat history?'), t("Attention : tout l'historique de conversation sera supprimé, " +
            "avec le contexte de l'assistant. La pipeline chat sera remise à zéro : " +
            `${CLEAR_STEP1_LABEL}.`, 'Warning: the full conversation history will be deleted, including the ' +
            'assistant context. The chat pipeline will reset to ' +
            `${CLEAR_STEP1_LABEL}.`), t('Effacer', 'Clear'), t('Annuler', 'Cancel'), t('La session MexaScreen reste intacte : récepteurs, peptides, dockings, ' +
            'calculs lancés et tout le panneau de gauche sont conservés.', 'Your MexaScreen session stays intact: receptors, peptides, dockings, ' +
            'running jobs, and everything in the left panel are kept.'));
        if (!confirmed)
            return;
        clearChatHistoryInFlight = true;
        const chatHistory = getChatHistory();
        setTyping(true);
        removeActionBanner();
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken) {
                setTyping(false);
                appendChatLine('bot', t('Session expirée. Reconnectez-vous pour utiliser l’assistant.', 'Session expired. Please sign in again to use the assistant.'));
                return;
            }
            const response = yield fetch(`${THREAD_URL}?project_id=${encodeURIComponent(projectId)}`, {
                method: 'DELETE',
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
            });
            const data = yield response.json().catch(() => ({}));
            setTyping(false);
            if (!response.ok) {
                const err = data.error ||
                    data.detail ||
                    `Clear failed (${response.status})`;
                appendChatLine('bot', String(err));
                return;
            }
            if (chatHistory)
                chatHistory.innerHTML = '';
            removeActionBanner();
            lastStepFicheKeyRef.current = '';
            currentPhaseRef.current = 'collecting_brief';
            applyPhaseUI('collecting_brief');
            setLastResponse(null);
            const welcome = String(data.ai_response
                || data.welcome
                || '').trim();
            if (welcome) {
                appendChatLine('bot', welcome);
            }
        }
        catch (err) {
            setTyping(false);
            console.error('[ChatbotLangGraph] Failed to clear chat history:', err);
            appendChatLine('bot', t("Impossible d'effacer l'historique. Réessayez.", 'Could not clear chat history. Please try again.'));
        }
        finally {
            clearChatHistoryInFlight = false;
        }
    });
}
const ChatbotLangGraph = () => {
    const isMountedRef = useRef(true);
    const abortRef = useRef(null);
    const restoreAbortRef = useRef(null);
    const currentPhaseRef = useRef('collecting_brief');
    const sendMessageRef = useRef(() => __awaiter(void 0, void 0, void 0, function* () { }));
    const lastStepFicheKeyRef = useRef('');
    const [debugOpen, setDebugOpen] = useState(false);
    const [lastResponse, setLastResponse] = useState(null);
    const sendMessage = useCallback((text_1, ...args_1) => __awaiter(void 0, [text_1, ...args_1], void 0, function* (text, approved = false, confirmPending = false, silentUser = false) {
        var _a, _b, _c, _d, _e;
        if (!text || !isMountedRef.current)
            return;
        removeActionBanner();
        // Hide machine / form payloads from the user bubble — but only treat true
        // Target-panel sync as a Target-sync turn (never form Launch / Continuer).
        const silentApprove = approved && /^(continue|continuer|ok|oui|yes|confirm|confirmer)$/i.test(text.trim());
        const isMachinePayload = isHiddenMachineUserText(text);
        const isTargetPanelSync = silentUser && /^__sync_main_target__\b/i.test(text.trim());
        const hideUserBubble = silentApprove || silentUser || isMachinePayload;
        if (!hideUserBubble) {
            appendChatLine('user', text);
        }
        const phaseBeforeSend = currentPhaseRef.current;
        setSendingLocked(true, phaseBeforeSend);
        setTyping(true);
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        abortRef.current = new AbortController();
        try {
            const msToken = yield getAccessTokenForOrchestrator();
            if (!msToken) {
                setTyping(false);
                appendChatLine('bot', 'Session expired. Please sign in again to use the assistant.');
                applyPhaseUI(currentPhaseRef.current);
                return;
            }
            // Navbar activeSessionId drives LangGraph thread_id (actor_id = Cognito sub on server).
            const projectId = resolveActiveProjectId();
            const response = yield fetch(CHAT_URL, {
                method: 'POST',
                headers: Object.assign(Object.assign({}, getAuthHeaders()), { Accept: 'application/json' }),
                body: JSON.stringify({
                    user_message: text,
                    project_id: projectId,
                    ms_token: msToken,
                    approved,
                    confirm_pending: confirmPending,
                }),
                signal: abortRef.current.signal,
            });
            const data = yield response.json().catch(() => ({}));
            if (!isMountedRef.current)
                return;
            setTyping(false);
            if (!response.ok) {
                const detail = data.detail;
                const detailText = detail
                    ? typeof detail === 'string'
                        ? detail
                        : JSON.stringify(detail)
                    : '';
                appendChatLine('bot', data.error || detailText || `Chat error (${response.status})`);
                applyPhaseUI(currentPhaseRef.current);
                return;
            }
            // Panel Target promo sync only — never rewrite form Launch / other silent turns.
            let chatData = data;
            const targetSwitchReturnedToReceptor = data.current_step === 'receptor_preparation' ||
                data.active_step === 'receptor_preparation';
            if (isTargetPanelSync && !targetSwitchReturnedToReceptor) {
                const reply = ((_c = (_b = data.ai_response) !== null && _b !== void 0 ? _b : data.message) !== null && _c !== void 0 ? _c : '').trim();
                const looksLikeTargetSwitch = /main\s+target\s+updated|target\s+principale\s+mise\s+à\s+jour/i.test(reply);
                const pdb = (typeof data.main_pdb === 'string' && data.main_pdb.trim()) ||
                    text.trim().split(/\s+/)[2] ||
                    '';
                chatData = Object.assign(Object.assign({}, data), { chat_notices: [], ai_response: looksLikeTargetSwitch
                        ? reply
                        : pdb
                            ? `Main Target updated → **${pdb.toUpperCase()}**. Pipeline defaults were reset for the new Target.`
                            : reply, 
                    // No pipeline redirect was needed: keep this machine turn quiet.
                    phase: 'collecting_brief', step_fiche: undefined, actions: [] });
            }
            // Keep frontend active session authoritative until backend supports multi-workspace.
            const username = getLoggedInUsername();
            const activeId = username
                ? getActiveSessionId(username)
                : parseInt(projectId, 10);
            if (chatData.ms_session_id != null && chatData.ms_session_id === activeId) {
                syncLangGraphProjectId(activeId);
            }
            else if (chatData.project_id === String(activeId)) {
                syncLangGraphProjectId(activeId);
            }
            if (username && ((_d = chatData.project_brief) === null || _d === void 0 ? void 0 : _d.target)) {
                updateProjectTarget(username, activeId, chatData.project_brief.target);
            }
            setLastResponse(chatData);
            const phase = handleChatResponse(chatData, () => {
                void sendMessageRef.current('continue', true);
            }, (pdb) => {
                void sendMessageRef.current(`__receptor_form_submit__ ${pdb}`, false, false, true);
            }, (pdb) => {
                void sendMessageRef.current(`__receptor_form_submit__ ${pdb}`, false, false, true);
            }, (sequence) => {
                void sendMessageRef.current(`__peptide_form_submit__ ${sequence}`, false, false, true);
            }, (binding) => {
                void sendMessageRef.current(binding, false, false, true);
            }, (docking) => {
                void sendMessageRef.current(docking, false, false, true);
            }, lastStepFicheKeyRef);
            currentPhaseRef.current = phase;
            yield ((_e = window.applyOrchestratorChatSync) !== null && _e !== void 0 ? _e : applyOrchestratorChatSync)(chatData);
        }
        catch (err) {
            if (!isMountedRef.current)
                return;
            if (err instanceof DOMException && err.name === 'AbortError')
                return;
            setTyping(false);
            console.error('[ChatbotLangGraph] /chat failed:', err);
            appendChatLine('bot', 'Could not reach the assistant. Is LangGraph running on port 8000?');
            applyPhaseUI(currentPhaseRef.current);
        }
        finally {
            if (isMountedRef.current) {
                setSendingLocked(false, currentPhaseRef.current);
                if (currentPhaseRef.current !== 'between_steps') {
                    const inputField = document.getElementById('chat-input');
                    inputField === null || inputField === void 0 ? void 0 : inputField.focus();
                }
            }
        }
    }), []);
    sendMessageRef.current = sendMessage;
    window.__langGraphSendConfirm = (payload) => {
        const isDismiss = payload.action === 'stay_on_step' ||
            payload.action === 'cancel_docking_launch' ||
            payload.action === 'cancel_set_main_target' ||
            payload.action === 'cancel_target_mismatch' ||
            payload.type === 'dismiss_action';
        if (isDismiss) {
            // Clear the proposal without advancing the pipeline.
            void sendMessageRef.current(payload.label, false, false, true);
            return;
        }
        void sendMessageRef.current(payload.label, true, true, true);
    };
    useEffect(() => {
        isMountedRef.current = true;
        const chatHistory = getChatHistory();
        if (chatHistory)
            chatHistory.innerHTML = '';
        const debugBtn = document.getElementById('chat-pipeline-debug-btn');
        if (debugBtn)
            debugBtn.style.display = '';
        const clearBtn = document.getElementById('chat-clear-history-btn');
        if (clearBtn)
            clearBtn.style.display = '';
        const onApprove = () => {
            void sendMessageRef.current('continue', true);
        };
        const onLaunchPdb = (pdb) => {
            void sendMessageRef.current(`__receptor_form_submit__ ${pdb}`, false, false, true);
        };
        const onSubmitBarePdb = (pdb) => {
            void sendMessageRef.current(`__receptor_form_submit__ ${pdb}`, false, false, true);
        };
        const onLaunchPeptide = (sequence) => {
            void sendMessageRef.current(`__peptide_form_submit__ ${sequence}`, false, false, true);
        };
        const onLaunchBindingSite = (value) => {
            void sendMessageRef.current(value, false, false, true);
        };
        const onLaunchDocking = (value) => {
            void sendMessageRef.current(value, false, false, true);
        };
        const restoreProjectId = resolveActiveProjectId();
        const restoreController = new AbortController();
        restoreAbortRef.current = restoreController;
        void restoreChatThread(restoreProjectId, onApprove, onLaunchPdb, onSubmitBarePdb, onLaunchPeptide, onLaunchBindingSite, onLaunchDocking, lastStepFicheKeyRef, setLastResponse, currentPhaseRef, restoreController.signal, () => isMountedRef.current && resolveActiveProjectId() === restoreProjectId);
        const handleSend = () => {
            const inputField = document.getElementById('chat-input');
            const text = inputField === null || inputField === void 0 ? void 0 : inputField.value.trim();
            if (!inputField || !text || inputField.disabled)
                return;
            inputField.value = '';
            void sendMessageRef.current(text, false);
        };
        const handleKeyDown = (e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.isComposing)
                return;
            e.preventDefault();
            e.stopPropagation();
            handleSend();
        };
        const handleClearHistory = (e) => {
            e.preventDefault();
            e.stopPropagation();
            void clearChatHistory(resolveActiveProjectId(), setLastResponse, currentPhaseRef, lastStepFicheKeyRef);
        };
        const sendButton = document.getElementById('chat-send');
        const inputField = document.getElementById('chat-input');
        sendButton === null || sendButton === void 0 ? void 0 : sendButton.addEventListener('click', handleSend);
        inputField === null || inputField === void 0 ? void 0 : inputField.addEventListener('keydown', handleKeyDown);
        clearBtn === null || clearBtn === void 0 ? void 0 : clearBtn.addEventListener('click', handleClearHistory);
        const onDebugClick = () => setDebugOpen(true);
        debugBtn === null || debugBtn === void 0 ? void 0 : debugBtn.addEventListener('click', onDebugClick);
        const unsubscribeAuth = onAuthStateChange((_user, authenticated) => {
            if (!authenticated)
                setDebugOpen(false);
        });
        const onMainTargetChanged = (ev) => {
            const detail = ev.detail || {};
            // Chat-driven switch already synced LangGraph state — only refresh panel marks.
            if (detail.skipAgentSync)
                return;
            const rid = detail.receptorID;
            const pdb = typeof detail.pdbID === 'string' ? detail.pdbID.trim().toUpperCase() : '';
            if (rid == null)
                return;
            // Machine sync only — never a user-visible chat line. Panel already wrote DynamoDB.
            const msg = pdb
                ? `__sync_main_target__ ${rid} ${pdb}`
                : `__sync_main_target__ ${rid}`;
            void sendMessageRef.current(msg, false, false, true);
        };
        window.addEventListener('mexa:main-target-changed', onMainTargetChanged);
        const onRequestMainTarget = (ev) => {
            const detail = ev.detail || {};
            const rid = Number(detail.receptorID);
            const pdb = typeof detail.pdbID === 'string' ? detail.pdbID.trim().toUpperCase() : '';
            if (!Number.isInteger(rid) || rid <= 0)
                return;
            const msg = pdb
                ? `__request_main_target__ ${rid} ${pdb}`
                : `__request_main_target__ ${rid}`;
            void sendMessageRef.current(msg, true, false, true);
        };
        window.addEventListener('mexa:request-main-target', onRequestMainTarget);
        return () => {
            var _a, _b;
            setDebugOpen(false);
            unsubscribeAuth();
            isMountedRef.current = false;
            (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            (_b = restoreAbortRef.current) === null || _b === void 0 ? void 0 : _b.abort();
            sendButton === null || sendButton === void 0 ? void 0 : sendButton.removeEventListener('click', handleSend);
            inputField === null || inputField === void 0 ? void 0 : inputField.removeEventListener('keydown', handleKeyDown);
            clearBtn === null || clearBtn === void 0 ? void 0 : clearBtn.removeEventListener('click', handleClearHistory);
            debugBtn === null || debugBtn === void 0 ? void 0 : debugBtn.removeEventListener('click', onDebugClick);
            window.removeEventListener('mexa:main-target-changed', onMainTargetChanged);
            window.removeEventListener('mexa:request-main-target', onRequestMainTarget);
            if (debugBtn)
                debugBtn.style.display = 'none';
            if (clearBtn)
                clearBtn.style.display = 'none';
            removeActionBanner();
        };
    }, []);
    return createPortal(React.createElement(PipelineDebugDrawer, { open: debugOpen, onClose: () => setDebugOpen(false), data: lastResponse }), document.body);
};
export default ChatbotLangGraph;
